/**
 * 管理后台 - 纠错管理页面（合并版）
 * 
 * 功能:
 * 1. 统一管理两种来源的纠错记录：
 *    - 手动提交：学员主动发起的纠错申请（有积分奖励）
 *    - 自动报错：学员做题时点击"报错"按钮提交（无积分奖励，发送站内信通知）
 * 2. 审核通过/拒绝
 * 3. 发放纠错奖励积分（仅手动提交）
 * 4. 发送站内信通知（仅自动报错）
 */

import React, { useEffect, useState } from 'react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import QuestionHtmlWithZoom from '../../components/QuestionHtmlWithZoom';
import { processQuestionHtml } from '../../utils/questionHtml';

interface Correction {
  id: string;
  submitter_id: string;
  submitter_role: string;
  target_type: string;
  target_id: string;
  error_description: string;
  suggested_correction: string | null;
  evidence_images: string[] | null;
  severity: string;
  status: string;
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: string | null;
  reward_points: number | null;
  created_at: string;
  submitter_username?: string;
  
  // 新增字段（来自 error_reports）
  question_content?: string | null;
  question_options?: any[] | null;
  paper_title?: string | null;
  error_type?: string | null;
  source?: string;  // 'manual' | 'auto_report'
  resolution_note?: string | null;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  question: '题目',
  paper: '试卷',
  answer: '答案',
  explanation: '解析',
};

const SEVERITY_LABELS: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '严重',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待审核',
  reviewing: '审核中',
  approved: '已通过',
  rejected: '已拒绝',
  processed: '已处理',
};

const SOURCE_LABELS: Record<string, string> = {
  manual: '手动提交',
  auto_report: '自动报错',
};

const ERROR_TYPE_LABELS: Record<string, string> = {
  answer_error: '答案错误',
  question_error: '题目错误',
  option_error: '选项错误',
  explanation_error: '解析错误',
  other: '其他问题',
};

const QUESTION_TYPE_MAP: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  multiple_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  coding: '编程题',
  python: 'Python编程',
  python_coding: 'Python编程',
  cpp: 'C++编程',
  cpp_coding: 'C++编程',
  graphic: '图形化编程',
  graphics: '图形化编程',
  scratch_coding: '图形化编程',
};

const getQuestionTypeName = (type: string): string => {
  return QUESTION_TYPE_MAP[type] || type;
};

const ErrorCorrectionReview: React.FC = () => {
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterErrorType, setFilterErrorType] = useState<string>('all');
  const [reviewComment, setReviewComment] = useState('');
  const [selectedCorrection, setSelectedCorrection] = useState<Correction | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchCorrections();
  }, [filterStatus, filterSource, filterErrorType]);

  const fetchCorrections = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('error_corrections')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }
      
      if (filterSource !== 'all') {
        query = query.eq('source', filterSource);
      }
      
      if (filterErrorType !== 'all') {
        query = query.eq('error_type', filterErrorType);
      }

      const { data } = await query;

      if (data) {
        // 获取提交人用户名
        const userIds = [...new Set(data.map(c => c.submitter_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);

        const profileMap = new Map((profiles || []).map(p => [p.id, p.username]));

        const correctionsWithUsername = data.map(c => ({
          ...c,
          submitter_username: profileMap.get(c.submitter_id) || '未知用户',
        }));

        setCorrections(correctionsWithUsername);
      }
    } catch (error) {
      console.error('Failed to fetch corrections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (correctionId: string, approved: boolean) => {
    try {
      const status = approved ? 'approved' : 'rejected';
      const correction = corrections.find(c => c.id === correctionId);
      
      const updateData: any = {
        status,
        reviewer_id: (await supabase.auth.getUser()).data.user?.id,
        review_comment: reviewComment || null,
        reviewed_at: new Date().toISOString(),
      };

      // 对于自动报错，使用 resolution_note 字段
      if (correction?.source === 'auto_report') {
        updateData.resolution_note = reviewComment || null;
      }
      
      const { error } = await supabase
        .from('error_corrections')
        .update(updateData)
        .eq('id', correctionId);

      if (error) {
        throw error;
      }

      // 如果通过且是手动提交，发放奖励积分
      if (approved && correction?.source === 'manual') {
        await grantReward(correction);
        alert('审核通过，已发放奖励积分');
      } else if (approved && correction?.source === 'auto_report') {
        // 自动报错通过后，发送站内信通知
        await sendNotification(correction, true);
        alert('审核通过，已发送通知');
      } else {
        // 拒绝时，如果是自动报错也发送通知
        if (correction?.source === 'auto_report') {
          await sendNotification(correction, false);
        }
        alert('已拒绝');
      }

      setShowModal(false);
      setReviewComment('');
      fetchCorrections();
    } catch (error) {
      console.error('Failed to review:', error);
      alert('操作失败');
    }
  };

  const grantReward = async (correction: Correction) => {
    try {
      // 获取奖励配置
      const { data: config } = await supabase
        .from('point_settings')
        .select('setting_value')
        .eq('setting_key', 'error_correction_reward')
        .single();

      const rewardPoints = config?.setting_value?.points || 50;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'error_correction_reward',
          correction_id: correction.id,
          submitter_id: correction.submitter_id,
          submitter_role: correction.submitter_role,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        console.error('Failed to grant reward:', result.error);
      }
    } catch (error) {
      console.error('Failed to grant reward:', error);
    }
  };

  const sendNotification = async (correction: Correction, approved: boolean) => {
    try {
      const statusLabel = approved ? '已通过' : '已拒绝';
      const title = `纠错反馈${statusLabel}`;

      // 构建题目信息
      const paperName = correction.paper_title || '未命名试卷';
      const questionType = correction.error_type ? ERROR_TYPE_LABELS[correction.error_type] || '未知类型' : '未知类型';

      // 构建 HTML 内容
      let content = `<div style="font-size: 14px; line-height: 1.6;">`;
      content += `<div style="margin-bottom: 12px; padding: 12px; background: #f0f7ff; border-radius: 8px; border-left: 4px solid #3b82f6;">`;
      content += `<div style="font-weight: bold; color: #1f2937; margin-bottom: 8px;">${paperName}</div>`;
      content += `<div style="color: #6b7280;">错误类型：${questionType}</div>`;
      content += `</div>`;
      content += `<div style="margin-bottom: 12px;"><strong>您的反馈：</strong>${correction.error_description}</div>`;
      content += `<div style="padding: 12px; background: ${approved ? '#f0fdf4' : '#fef2f2'}; border-radius: 8px; border-left: 4px solid ${approved ? '#22c55e' : '#ef4444'};">`;
      content += `<div style="font-weight: bold; color: #1f2937;">处理结果：${statusLabel}</div>`;
      if (reviewComment) {
        content += `<div style="color: #6b7280; margin-top: 8px;">审核意见：${reviewComment}</div>`;
      }
      content += `</div></div>`;

      // 调用消息服务发送站内信
      await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send',
          recipient_id: correction.submitter_id,
          title,
          content,
          scene: 'error_correction_review',
        }),
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  };

  const openReviewModal = (correction: Correction) => {
    setSelectedCorrection(correction);
    setReviewComment('');
    setShowModal(true);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">纠错管理</h1>
        <p className="text-sm text-gray-500 mt-1">统一管理学员提交的纠错反馈和自动报错</p>
      </div>

      {/* 筛选器 */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">状态:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="pending">待审核</option>
              <option value="reviewing">审核中</option>
              <option value="approved">已通过</option>
              <option value="rejected">已拒绝</option>
              <option value="processed">已处理</option>
              <option value="all">全部</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">来源:</span>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">全部</option>
              <option value="manual">手动提交</option>
              <option value="auto_report">自动报错</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">错误类型:</span>
            <select
              value={filterErrorType}
              onChange={(e) => setFilterErrorType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">全部</option>
              <option value="answer_error">答案错误</option>
              <option value="question_error">题目错误</option>
              <option value="option_error">选项错误</option>
              <option value="explanation_error">解析错误</option>
              <option value="other">其他问题</option>
            </select>
          </div>
        </div>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">加载中...</div>
      ) : corrections.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📝</div>
          <div className="text-gray-500">暂无纠错记录</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">提交人</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">来源</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">纠错对象</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">错误类型</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">严重程度</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">状态</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">提交时间</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody>
              {corrections.map((correction) => (
                <tr key={correction.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">{correction.submitter_username}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      correction.source === 'auto_report' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {SOURCE_LABELS[correction.source || 'manual']}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div>{TARGET_TYPE_LABELS[correction.target_type] || '题目'}</div>
                    {correction.paper_title && (
                      <div className="text-xs text-gray-400">{correction.paper_title}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {correction.error_type ? (
                      <span className="text-xs text-gray-600">{ERROR_TYPE_LABELS[correction.error_type] || correction.error_type}</span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      correction.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      correction.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                      correction.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {SEVERITY_LABELS[correction.severity]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      correction.status === 'approved' ? 'bg-green-100 text-green-700' :
                      correction.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      correction.status === 'processed' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {STATUS_LABELS[correction.status]}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-500">{formatDate(correction.created_at)}</td>
                  <td className="py-3 px-4">
                    {correction.status === 'pending' && (
                      <button
                        onClick={() => openReviewModal(correction)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        审核
                      </button>
                    )}
                    {correction.status !== 'pending' && (
                      <span className="text-gray-400 text-sm">已处理</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 审核弹窗 */}
      {showModal && selectedCorrection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              审核纠错 - {SOURCE_LABELS[selectedCorrection.source || 'manual']}
            </h3>
            
            <div className="space-y-3 mb-4 text-sm">
              <div>
                <span className="text-gray-500">提交人:</span>
                <span className="ml-2">{selectedCorrection.submitter_username}</span>
              </div>
              
              {/* 自动报错来源展示 */}
              {selectedCorrection.source === 'auto_report' ? (
                <>
                  <div>
                    <span className="text-gray-500">试卷:</span>
                    <span className="ml-2">{selectedCorrection.paper_title || '未命名'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">错误类型:</span>
                    <span className="ml-2">{ERROR_TYPE_LABELS[selectedCorrection.error_type || ''] || selectedCorrection.error_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">错误描述:</span>
                    <p className="mt-1 text-gray-700">{selectedCorrection.error_description}</p>
                  </div>
                  {/* 显示题目内容 */}
                  {selectedCorrection.question_content && (
                    <div className="mt-4">
                      <span className="text-gray-500 block mb-2">题目内容:</span>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <QuestionHtmlWithZoom html={selectedCorrection.question_content} />
                      </div>
                    </div>
                  )}
                  {/* 显示选项 */}
                  {selectedCorrection.question_options && selectedCorrection.question_options.length > 0 && (
                    <div className="mt-4">
                      <span className="text-gray-500 block mb-2">选项:</span>
                      <div className="space-y-2">
                        {selectedCorrection.question_options.map((opt: any, idx: number) => {
                          const optId = opt.id || opt.label || String.fromCharCode(65 + idx);
                          const optText = processQuestionHtml(opt.text || '');
                          return (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                              <span className="font-medium text-gray-700 min-w-[24px]">{optId}.</span>
                              <div dangerouslySetInnerHTML={{ __html: optText }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* 手动提交来源展示 */
                <>
                  <div>
                    <span className="text-gray-500">纠错对象:</span>
                    <span className="ml-2">{TARGET_TYPE_LABELS[selectedCorrection.target_type]}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">严重程度:</span>
                    <span className="ml-2">{SEVERITY_LABELS[selectedCorrection.severity]}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">错误描述:</span>
                    <p className="mt-1 text-gray-700">{selectedCorrection.error_description}</p>
                  </div>
                  {selectedCorrection.suggested_correction && (
                    <div>
                      <span className="text-gray-500">建议修正:</span>
                      <p className="mt-1 text-gray-700">{selectedCorrection.suggested_correction}</p>
                    </div>
                  )}
                  {selectedCorrection.evidence_images && selectedCorrection.evidence_images.length > 0 && (
                    <div>
                      <span className="text-gray-500">证据图片:</span>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {selectedCorrection.evidence_images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`证据${idx + 1}`}
                            className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                审核意见{selectedCorrection.source === 'auto_report' ? '（将发送给学员）' : ''}
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="请输入审核意见..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleReview(selectedCorrection.id, true)}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {selectedCorrection.source === 'manual' ? '通过并发放奖励' : '通过并通知'}
              </button>
              <button
                onClick={() => handleReview(selectedCorrection.id, false)}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                拒绝
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorCorrectionReview;
